/**
 * Recovering from a stale lazy-loaded chunk.
 *
 * Route components are code-split, so navigating to a view fetches a chunk over
 * the network. That introduces a failure the app did not have while everything
 * shipped in one bundle, and it has a **daily** trigger:
 *
 *   - The frontend deploy prunes the nginx bundle directory by mtime, so every
 *     chunk from the previous build is deleted the moment a new one lands.
 *   - Production deploys at 07:00 Brisbane, every day.
 *   - A person who left a tab open overnight is holding an `index.html` that
 *     names chunk hashes which no longer exist. Their next navigation requests
 *     a 404, `lazy()` rejects, and React unmounts the tree — a white screen
 *     across the whole authenticated app, not just the view they asked for.
 *
 * Reloading fixes it completely: the fresh `index.html` names the chunks that
 * are actually on disk. So a chunk error is reloaded automatically, **once**.
 * The one-shot guard matters — if the reload does not fix it (a genuine outage,
 * an offline device) a second reload would loop the browser forever. After one
 * attempt the error boundary's own UI is shown instead, which offers a manual
 * refresh and says what went wrong.
 */

/** Session key recording that a reload has already been attempted. */
const RELOAD_ATTEMPTED_KEY = 'gra:chunk-reload-attempted';

/**
 * Does this error mean a dynamic import failed to load?
 *
 * The message differs per browser and per bundler, and none of them expose a
 * stable error code, so this matches on the known phrasings: Vite/Chrome,
 * Firefox, Safari, and webpack's `ChunkLoadError` for good measure.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const err = error as { name?: string; message?: string };
  const text = `${err.name ?? ''} ${err.message ?? ''}`;
  return (
    /ChunkLoadError/i.test(text) ||
    /Loading chunk [\w-]+ failed/i.test(text) ||
    /dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text) ||
    /error loading dynamically imported module/i.test(text)
  );
}

/** `sessionStorage` throws in some privacy modes; never let that mask the error. */
function readAttempted(): boolean {
  try {
    return window.sessionStorage.getItem(RELOAD_ATTEMPTED_KEY) === '1';
  } catch {
    // No session storage means no guard, so treat it as "already tried" rather
    // than risk a reload loop we cannot count.
    return true;
  }
}

function markAttempted(): void {
  try {
    window.sessionStorage.setItem(RELOAD_ATTEMPTED_KEY, '1');
  } catch {
    // Ignored — readAttempted() fails closed, so we simply do not reload.
  }
}

/**
 * Clear the guard once the app is running normally again, so a stale chunk
 * encountered tomorrow is still recovered automatically. Call this from a
 * successful render, never from the error path.
 */
export function clearChunkReloadGuard(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_ATTEMPTED_KEY);
  } catch {
    // Ignored.
  }
}

/** The real reload. Injected below so the decision logic stays testable. */
const reloadPage = (): void => window.location.reload();

/**
 * Reload once if `error` is a failed chunk fetch. Returns true when a reload
 * was triggered, so callers can skip rendering an error UI that is about to be
 * thrown away.
 *
 * `reload` is a parameter because jsdom makes `window.location` non-writable,
 * so the one behaviour that most needs a test — that this fires exactly once —
 * is otherwise unobservable. Production callers pass nothing.
 */
export function recoverFromChunkLoadError(
  error: unknown,
  reload: () => void = reloadPage
): boolean {
  if (!isChunkLoadError(error)) return false;
  if (readAttempted()) return false;

  markAttempted();
  console.warn(
    '[chunkReload] A view chunk failed to load — reloading once to pick up the current build.'
  );
  reload();
  return true;
}
